import { normalizeManifestI18n } from "../../intl/manifestI18n";
import { normalizeI18nRollout } from "../../intl/i18nRollout.js";

export const CASAMAPA_CLIENT_VIEW_SLUG = "casamapa";
export const CASAMAPA_PRODUCT_KEY = "casamapa";
export const CASAMAPA_SLEEK_HOME_LAYOUT = "casamapa-sleek";

export const CASAMAPA_RUNTIME_I18N = {
  defaultLocale: "es-CO",
  supportedLocales: ["es-CO", "en-US", "ru-RU"],
  defaultCurrency: "COP",
  supportedCurrencies: ["COP", "USD", "EUR", "RUB"],
};

export const CASAMAPA_I18N_ROLLOUT = {
  clientViewI18nEnabled: true,
  localizedRoutesEnabled: true,
  localizedSeoEnabled: true,
  displayCurrencyEnabled: true,
  fxConversionEnabled: true,
  studioTranslationEditorEnabled: true,
  translationImportEnabled: true,
  missingTranslationWarningsEnabled: true,
};

/** Default GA4 property for CasaMapa public Client View. */
export const CASAMAPA_GA4_MEASUREMENT_ID = "G-QH14LQRD0M";
export const CASAMAPA_GA4_ENV_KEY = "REACT_APP_CASAMAPA_GA4_ID";

function uniqueMergeList(preferred, fallback) {
  const seen = new Set();
  const result = [];
  for (const value of [...(preferred || []), ...(fallback || [])]) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Buyer locales and currencies always union with product defaults so adding a
 * supported language or currency (for example ru-RU or RUB) takes effect even
 * when a published manifest already lists a shorter set.
 */
function mergeRuntimeSupportedLocales(manifestList, runtimeDefaults) {
  return uniqueMergeList(manifestList, runtimeDefaults);
}

function mergeRuntimeSupportedCurrencies(manifestList, runtimeDefaults) {
  return uniqueMergeList(manifestList, runtimeDefaults);
}

function getHomePage(manifest) {
  return (manifest?.pages || []).find((page) => page.template === "home") || null;
}

/**
 * Detect CasaMapa only via explicit signals (slug, home layout, or product code).
 * Never treat generic `canonical_real_estate` views (e.g. Golf Homes) as CasaMapa.
 */
export function isCasaMapaExperienceManifest(manifest, slug = null) {
  if (slug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return true;
  }
  if (manifest?.slug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return true;
  }
  const productKey =
    manifest?.productKey ||
    manifest?.config?.productKey ||
    manifest?.config?.clientViewCode ||
    null;
  if (productKey === CASAMAPA_PRODUCT_KEY) {
    return true;
  }
  const homePage = getHomePage(manifest);
  if (homePage?.content?.homeLayout === CASAMAPA_SLEEK_HOME_LAYOUT) {
    return true;
  }
  return false;
}

export function enrichCasaMapaManifest(manifest, slug = null) {
  if (!manifest || !isCasaMapaExperienceManifest(manifest, slug)) {
    return manifest;
  }

  const current = manifest.i18n || {};
  const mergedRaw = {
    ...CASAMAPA_RUNTIME_I18N,
    ...current,
    supportedLocales: mergeRuntimeSupportedLocales(
      current.supportedLocales,
      CASAMAPA_RUNTIME_I18N.supportedLocales
    ),
    supportedCurrencies: mergeRuntimeSupportedCurrencies(
      current.supportedCurrencies,
      CASAMAPA_RUNTIME_I18N.supportedCurrencies
    ),
  };

  const mergedI18n = normalizeManifestI18n(mergedRaw);
  const rollout = normalizeI18nRollout(
    {
      ...CASAMAPA_I18N_ROLLOUT,
      ...(current.rollout || {}),
    },
    mergedI18n
  );

  const pages = (manifest.pages || []).map((page) => {
    if (page.template !== "home") {
      return page;
    }
    return {
      ...page,
      content: {
        ...(page.content || {}),
        homeLayout: page.content?.homeLayout || CASAMAPA_SLEEK_HOME_LAYOUT,
      },
    };
  });

  const hasPrivacy = pages.some((page) => {
    const route = String(page.route || "");
    return route === "/privacidad" || /privacidad/i.test(route);
  });
  if (!hasPrivacy) {
    pages.push({
      route: "/privacidad",
      template: "content",
      title: "Privacidad",
      seo: {
        title: "Privacidad | CasaMapa",
        description: "Aviso de tratamiento de datos personales de CasaMapa.",
      },
      content: {
        body:
          "CasaMapa trata los datos personales que envías en una solicitud de información sobre inmuebles para gestionar tu consulta y compartirla con el representante correspondiente de la propiedad.",
      },
    });
  }

  const existingAnalytics = manifest.analytics || {};
  const existingGa = existingAnalytics.googleAnalytics || {};
  const measurementId =
    (typeof existingGa.measurementId === "string" && existingGa.measurementId.trim()) ||
    CASAMAPA_GA4_MEASUREMENT_ID;

  return {
    ...manifest,
    pages,
    features: {
      ...(manifest.features || {}),
      contactAgent: manifest.features?.contactAgent !== false,
      leadSubmission: manifest.features?.leadSubmission !== false,
      favorites: true,
      saveSearch: true,
      savedSearchAlerts: true,
    },
    analytics: {
      ...existingAnalytics,
      enabled: existingAnalytics.enabled !== false,
      clientViewCode: existingAnalytics.clientViewCode || CASAMAPA_CLIENT_VIEW_SLUG,
      googleAnalytics: {
        ...existingGa,
        enabled: true,
        measurementId,
        measurementIdEnvKey: existingGa.measurementIdEnvKey || CASAMAPA_GA4_ENV_KEY,
        dataSource: {
          enabled: existingGa.dataSource?.enabled !== false,
          propertyIdEnvKey:
            existingGa.dataSource?.propertyIdEnvKey || "GA4_CASAMAPA_PROPERTY_ID",
          pollIntervalMinutes: existingGa.dataSource?.pollIntervalMinutes || 60,
          syncLookbackDays: existingGa.dataSource?.syncLookbackDays || 7,
          ...(existingGa.dataSource || {}),
        },
      },
      tracking: {
        pageViews: true,
        ctaClicks: true,
        contactFormSubmissions: true,
        sourceAttribution: true,
        ...(existingAnalytics.tracking || {}),
      },
    },
    i18n: {
      ...mergedI18n,
      rollout,
    },
  };
}
